import React from 'react';
import { useDispatch } from 'react-redux';

import InvestorProfile from 'app/InvestorOutreach/Profile';
import { getMapCardPhoto, showMapCardNoPhotoOnError } from 'app/InvestorOutreach/propertyPhoto';
import { getPrimaryInvestorType } from 'app/InvestorOutreach/investorTypeTheme';
import { formatCount, formatPrice, formatPropertyAddress } from 'data/investors/investorUtils';

import { Metric, TypeTag } from './index';
import css from './style.scss';

// The investor's property as the Investor Outreach card shows it (ATAI-520
// reopen), for when PropStream can't load that property for the full compact
// property card. The investor service's `propertyId` doesn't always resolve in
// PropStream, which used to show "Property not found" and then the investor
// summary instead of the property the investor card lists under "Owned
// Properties". Same layout as InvestorLeadCard, filled from the investor's own
// property record: photo, address and facts.
//
// The record comes either from the stored profile (the search's own property,
// one `addressLine`) or from the owned-properties API (split address fields).
export const formatAddress = (property) => {
  const line = formatPropertyAddress(property);
  if (line) return line;

  const { address, city, state, zip } = property || {};
  // `address` is a street string on the owned-properties rows, but an object
  // on some stored records.
  const street = address && typeof address === 'object'
    ? address.streetAddress || address.street || address.line1 || address.fullAddress
    : address;
  // No street, no address: city/state/zip alone isn't a property to show.
  if (typeof street !== 'string' || !street.trim()) return '';

  const region = [state || (address && address.state), zip || (address && address.zip)].filter(Boolean).join(' ');
  return [street, city || (address && address.city), region].filter(Boolean).join(', ');
};

const InvestorPropertyCard = ({ investorId, contactId, profile, property }) => {
  const dispatch = useDispatch();
  const type = getPrimaryInvestorType(profile && profile.classifications) || 'INVESTOR';
  const address = formatAddress(property);

  // Property Details can't open a property PropStream couldn't load, so Details
  // opens the investor's profile, as on the investor summary card.
  const openDetails = () =>
    dispatch(InvestorProfile.open({ investor: { id: investorId, contactId, saved: true } }));

  return (
    <div className={css.card}>
      <div className={css.image}>
        <img
          className={css.image__photo}
          src={getMapCardPhoto(property)}
          onError={showMapCardNoPhotoOnError}
          alt=""
        />
        <button type="button" className={css.details} onClick={openDetails}>
          Details
        </button>
      </div>

      <div className={css.body}>
        <div className={css.tags}>
          <TypeTag type={type} />
        </div>

        <div className={css.headline}>
          <span className={css.headline__value}>{formatPrice(property.lastSalePrice) || '—'}</span>
          <span className={css.headline__label}>Last Sale Price</span>
        </div>

        <p className={css.address} title={address || undefined}>
          {address || 'Address unavailable'}
        </p>

        <div className={css.metrics}>
          <Metric label="Beds" value={formatCount(property.bedrooms)} />
          <Metric label="Baths" value={formatCount(property.bathrooms)} />
          <Metric label="Sq Ft" value={formatCount(property.livingAreaSquareFeet)} />
          <Metric label="Lot Sq Ft" value={formatCount(property.lotSquareFeet)} />
        </div>
      </div>
    </div>
  );
};

export default InvestorPropertyCard;
